import { Link } from "react-router-dom";
import { useState } from "react";
import { getVideoMeta } from "@/data/videos";
import { Play, ExternalLink, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhoneFrame } from "./PhoneFrame";

interface Video {
  id: string;
  title: string;
}

interface VideoSectionProps {
  anchor: string;
  sectionTitle: string;
  highlightedTitle: string;
  videos: Video[];
  seeMoreUrl: string;
  seeMoreLabel: string;
}

const THUMB_FORMATS = ["maxresdefault.jpg", "hq720.jpg", "sddefault.jpg", "hqdefault.jpg"];

function LazyPhoneVideo({ video }: { video: Video }) {
  const [loaded, setLoaded] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const metaName = getVideoMeta(video.id).name;
  const thumbnailUrl = `https://img.youtube.com/vi/${video.id}/${THUMB_FORMATS[thumbIdx]}`;

  return (
    <div className="flex flex-col items-center">
      <PhoneFrame>
        {loaded ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={metaName}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play video: ${video.title}`}
          >
            <img
              src={thumbnailUrl}
              alt={metaName}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => {
                if (thumbIdx < THUMB_FORMATS.length - 1) setThumbIdx((i) => i + 1);
              }}
            />
            <div className="absolute inset-0 bg-background/30 transition-colors duration-300 group-hover:bg-background/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary shadow-gold transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 h-6 w-6 fill-secondary-foreground text-secondary-foreground" />
              </div>
            </div>
          </button>
        )}
      </PhoneFrame>
      <div className="mt-5 max-w-[260px] text-center">
        <p className="text-sm font-semibold leading-snug text-foreground">{video.title}</p>
      </div>
    </div>
  );
}

function VideoSection({ anchor, sectionTitle, highlightedTitle, videos, seeMoreUrl, seeMoreLabel }: VideoSectionProps) {
  return (
    <div id={anchor} className="scroll-mt-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <h2 className="text-2xl md:text-3xl font-bold">
          {sectionTitle} <span className="text-gradient-gold">{highlightedTitle}</span>
        </h2>
        <a
          href={seeMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors shrink-0"
        >
          <Youtube className="w-4 h-4" />
          {seeMoreLabel}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {videos.map((video) => (
          <LazyPhoneVideo key={video.id} video={video} />
        ))}
      </div>
    </div>
  );
}

const sections: VideoSectionProps[] = [
  {
    anchor: "retaining-walls",
    sectionTitle: "Retaining",
    highlightedTitle: "Walls",
    seeMoreLabel: "See More Retaining Walls",
    seeMoreUrl: "https://www.youtube.com/@MasonryProConstruction/search?query=retaining%20wall",
    videos: [
      { id: "ktQRtcBf70o", title: "Before & After: New Retaining Wall Installation in Morris County, NJ" },
      { id: "-8-f3fV09Lw", title: "Large Engineered Retaining Wall Installation in Hackensack, NJ" },
      { id: "g25VA4vng-Y", title: "New Cambridge Block Retaining Wall in Nutley, NJ" },
      { id: "w9nUyl2SgLU", title: "Stunning Retaining Wall Installation in Passaic County, NJ" },
      { id: "aJJQS6FX--0", title: "Retaining Wall & Paver Driveway Installation in Kinnelon, NJ" },
    ],
  },
  {
    anchor: "paver-patios",
    sectionTitle: "Paver Patios &",
    highlightedTitle: "Outdoor Living",
    seeMoreLabel: "See More Paver Patios",
    seeMoreUrl: "https://www.youtube.com/@MasonryProConstruction/search?query=paver%20patio",
    videos: [
      { id: "sbUEKc_QU78", title: "Custom Paver Patio, Steps & Outdoor Kitchen Build" },
      { id: "KFgLmBVqRZQ", title: "Paver Patio, Outdoor Kitchen & Fireplace Installation" },
      { id: "Fogsfyz7P18", title: "Paver Patio with Outdoor Kitchen & Fire Pit" },
      { id: "M16CRt8dmJ4", title: "Pool Paver Patio, Outdoor Kitchen & Fire Pit" },
      { id: "plh-ZGZz65A", title: "Paver Patio & Fire Pit Installation" },
    ],
  },
  {
    anchor: "driveways-walkways",
    sectionTitle: "Driveways, Walkways",
    highlightedTitle: "& Steps",
    seeMoreLabel: "See More Driveways & Walkways",
    seeMoreUrl: "https://www.youtube.com/@MasonryProConstruction/search?query=walkway%20steps",
    videos: [
      { id: "50WpbxLOZa8", title: "Paver Driveway Installation in Oakland, NJ" },
      { id: "xSJR_H3armE", title: "Paver Driveway & Walkway Installation in Cedar Grove, NJ" },
      { id: "Z1lcOlPIbWI", title: "Beautiful Paver Walkway & Block Steps in North Caldwell, NJ" },
      { id: "c_50pnhg30w", title: "Walkway, Masonry Steps & Stone Veneer in Wayne, NJ" },
      { id: "rqWp1V0r8sY", title: "Steps, Walkway & Retaining Wall in Essex County, NJ" },
      { id: "48t7raf6QiE", title: "Retaining Wall, Steps & Walkway in Bergen County, NJ" },
    ],
  },
  {
    anchor: "stone-veneer",
    sectionTitle: "Stone",
    highlightedTitle: "Veneer",
    seeMoreLabel: "See More Stone Veneer",
    seeMoreUrl: "https://www.youtube.com/@MasonryProConstruction/search?query=stone%20veneer",
    videos: [
      { id: "t2rG1WND0ug", title: "Stone Veneer Installation in Wayne, NJ" },
      { id: "0Kid2ea7tik", title: "Stone Veneer Installation in Oakland, NJ" },
    ],
  },
];

const anchors = [
  { label: "Retaining Walls", href: "#retaining-walls" },
  { label: "Paver Patios", href: "#paver-patios" },
  { label: "Driveways & Walkways", href: "#driveways-walkways" },
  { label: "Stone Veneer", href: "#stone-veneer" },
];

export function VideoPortfolioSection() {
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Watch Our <span className="text-gradient-gold">Work in Action</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse project videos from across North New Jersey: retaining walls, patios, driveways, and more. Want to see the work move? Browse our <Link to="/videos" className="text-secondary hover:underline">project videos</Link>.
          </p>
        </div>

        {/* Anchor nav */}
        <nav className="flex flex-wrap justify-center gap-3 mb-16" aria-label="Video sections">
          {anchors.map((anchor) => (
            <a
              key={anchor.href}
              href={anchor.href}
              className="px-4 py-2 rounded-full border-2 border-secondary text-secondary text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors duration-200"
            >
              {anchor.label}
            </a>
          ))}
        </nav>

        {/* Video sections */}
        <div className="space-y-20">
          {sections.map((section) => (
            <VideoSection key={section.anchor} {...section} />
          ))}
        </div>

        {/* Full Portfolio CTA */}
        <div id="full-portfolio" className="scroll-mt-24 mt-24 rounded-2xl bg-card border border-border p-10 md:p-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
            <Youtube className="w-8 h-8 text-secondary" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Our Full <span className="text-gradient-gold">Video Portfolio</span>
          </h3>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Completed projects documented on our YouTube channel. Subscribe to stay updated on our latest work across North New Jersey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="gold" size="lg" asChild>
              <a
                href="https://www.youtube.com/@MasonryProConstruction"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <Youtube className="w-5 h-5" />
                View Full Portfolio on YouTube
              </a>
            </Button>
            <Button variant="goldOutline" size="lg" asChild>
              <a
                href="https://www.youtube.com/@MasonryProConstruction/shorts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                All Shorts
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
            <Button variant="goldOutline" size="lg" asChild>
              <a
                href="https://www.youtube.com/@MasonryProConstruction/videos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                All Videos
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
